<script setup lang="ts">
import { reactive, watch } from "vue";
import Button from "primevue/button";
import Select from "primevue/select";
import Tag from "primevue/tag";
import Textarea from "primevue/textarea";
import Message from "primevue/message";
import { useToast } from "primevue/usetoast";

import Markdown from "./Markdown.vue";
import { isCtrlEnter } from "../keys";
import {
  finalizeAsOwner,
  respondAsContributor,
  type Contributor,
  type ContributorDecision,
  type OwnerDecision,
  type QualityEvent,
  type Severity,
} from "../api";

/**
 * Renders one quality event in full: header, meta, description, contributor
 * rows, and (where allowed) the respond / finalize action forms.
 *
 * Used by both ReviewDrawer (one EventDetail per event in the review) and
 * EventDrawer (one EventDetail for a standalone event). The two-step submit
 * pattern lives here too.
 */
const props = defineProps<{
  event: QualityEvent;
  email: string | null;
  isManager: boolean;
}>();

const emit = defineEmits<{ (e: "changed"): void }>();

const toast = useToast();

type FormStage = "idle" | "confirming" | "submitting";

interface RespondDraft {
  decision: ContributorDecision;
  comment: string;
  stage: FormStage;
  error: string;
}
interface FinalizeDraft {
  decision: OwnerDecision;
  amendedSeverity: Severity;
  comment: string;
  stage: FormStage;
  error: string;
}

const respondDrafts = reactive<Record<number, RespondDraft>>({});
const finalizeDrafts = reactive<Record<number, FinalizeDraft>>({});

function respondDraft(cid: number): RespondDraft {
  if (!respondDrafts[cid]) {
    respondDrafts[cid] = {
      decision: "agree",
      comment: "",
      stage: "idle",
      error: "",
    };
  }
  return respondDrafts[cid];
}
function finalizeDraft(cid: number): FinalizeDraft {
  if (!finalizeDrafts[cid]) {
    finalizeDrafts[cid] = {
      decision: "preserve",
      amendedSeverity: "medium",
      comment: "",
      stage: "idle",
      error: "",
    };
  }
  return finalizeDrafts[cid];
}

watch(
  () => props.event.id,
  () => {
    for (const k of Object.keys(respondDrafts)) delete respondDrafts[Number(k)];
    for (const k of Object.keys(finalizeDrafts)) delete finalizeDrafts[Number(k)];
  },
);

// The contributor picks Agree or Dispute. "Agree, with comment" is no longer
// a separate choice — leaving a comment under Agree records it automatically
// (see effectiveDecision). The full three-value model still lives in the
// backend enum, so existing data and labels keep working.
const decisionOptions: { label: string; value: ContributorDecision }[] = [
  { label: "Agree", value: "agree" },
  { label: "Dispute", value: "dispute" },
];
const decisionLabels: Record<ContributorDecision, string> = {
  agree: "Agree",
  agree_with_comment: "Agree, with comment",
  dispute: "Dispute",
};
const ownerDecisionOptions: { label: string; value: OwnerDecision }[] = [
  { label: "Preserve as-is", value: "preserve" },
  { label: "Amend severity", value: "amend" },
  { label: "Nullify", value: "nullify" },
];
const severityOptions: { label: string; value: Severity }[] = [
  { label: "low", value: "low" },
  { label: "medium", value: "medium" },
  { label: "high", value: "high" },
  { label: "critical", value: "critical" },
];

function fmt(d: string | null): string {
  if (!d) return "—";
  return new Date(d).toLocaleString();
}
function decisionLabel(d: ContributorDecision): string {
  return decisionLabels[d] ?? d;
}
// A comment under "Agree" upgrades the decision to "agree_with_comment".
// Dispute is unchanged (it always carries a comment). Used for both the
// confirm-screen label and the value actually submitted.
function effectiveDecision(d: RespondDraft): ContributorDecision {
  return d.decision === "agree" && d.comment.trim()
    ? "agree_with_comment"
    : d.decision;
}
function ownerDecisionLabel(d: OwnerDecision): string {
  const o = ownerDecisionOptions.find((x) => x.value === d);
  return o ? o.label : d;
}

function canIRespond(c: Contributor): boolean {
  return c.contributor === props.email && c.contributor_decision === null;
}
function canIFinalize(c: Contributor): boolean {
  if (props.event.status === "closed") return false;
  return (
    props.event.owner === props.email &&
    c.contributor_decision !== null &&
    c.owner_decision === null
  );
}

// --- two-step respond -----------------------------------------------------

function startRespond(cid: number): void {
  const d = respondDraft(cid);
  d.error = "";
  if (d.decision === "dispute" && !d.comment.trim()) {
    d.error = "A comment is required when you dispute an event.";
    return;
  }
  d.stage = "confirming";
}
function cancelRespond(cid: number): void {
  const d = respondDraft(cid);
  d.stage = "idle";
  d.error = "";
}
async function confirmRespond(cid: number): Promise<void> {
  const d = respondDraft(cid);
  const decision = effectiveDecision(d);
  d.stage = "submitting";
  d.error = "";
  try {
    await respondAsContributor(props.event.id, cid, decision, d.comment || null);
    toast.add({
      severity: "success",
      summary: "Response recorded",
      detail: `Your decision: ${decisionLabel(decision)}.`,
      life: 3000,
    });
    emit("changed");
  } catch (e) {
    d.stage = "confirming";
    d.error = String(e);
  }
}

// --- two-step finalize ----------------------------------------------------

function startFinalize(cid: number): void {
  const d = finalizeDraft(cid);
  d.error = "";
  d.stage = "confirming";
}
function cancelFinalize(cid: number): void {
  const d = finalizeDraft(cid);
  d.stage = "idle";
  d.error = "";
}
async function confirmFinalize(cid: number): Promise<void> {
  const d = finalizeDraft(cid);
  d.stage = "submitting";
  d.error = "";
  try {
    const sev = d.decision === "amend" ? d.amendedSeverity : null;
    await finalizeAsOwner(props.event.id, cid, d.decision, sev, d.comment || null);
    toast.add({
      severity: "success",
      summary: "Finalised",
      detail: `Decision: ${ownerDecisionLabel(d.decision)}.`,
      life: 3000,
    });
    emit("changed");
  } catch (e) {
    d.stage = "confirming";
    d.error = String(e);
  }
}
</script>

<template>
  <div class="event-detail" :data-event-id="event.id" :data-status="event.status">
    <!--
      The wrapping component (ReviewDrawer's per-event Panel header,
      EventDrawer's head-band) renders the severity + status + summary banner.
      We don't repeat it here.
    -->
    <dl class="meta">
      <div><dt>Category</dt><dd>{{ event.category }}</dd></div>
      <div><dt>Owner</dt><dd>{{ event.owner }}</dd></div>
      <div v-if="event.reporter"><dt>Reporter</dt><dd>{{ event.reporter }}</dd></div>
      <div v-if="event.policy_document">
        <dt>Policy</dt>
        <dd>{{ event.policy_document }} §{{ event.policy_section || "—" }}</dd>
      </div>
    </dl>

    <div class="description">
      <Markdown :source="event.description" />
    </div>

    <h4 v-if="event.contributors.length" class="subsection-h">Contributors</h4>
    <div
      v-for="c in event.contributors"
      :key="c.id"
      class="contrib"
      :data-contrib-id="c.id"
    >
      <div class="contrib-head">
        <span class="contrib-email">{{ c.contributor }}</span>
        <Tag
          v-if="c.contributor_decision"
          :value="decisionLabel(c.contributor_decision)"
          :severity="c.contributor_decision === 'dispute' ? 'danger' : 'success'"
        />
        <Tag v-else value="awaiting response" severity="secondary" />
        <span
          v-if="c.contributor_decision_date"
          class="contrib-time"
          :title="c.contributor_decision_date"
        >
          {{ fmt(c.contributor_decision_date) }}
        </span>
      </div>

      <div v-if="c.contributor_comment" class="contrib-comment">
        <strong>Their comment:</strong>
        <Markdown :source="c.contributor_comment" />
      </div>

      <div v-if="c.owner_decision" class="owner-block">
        <div class="contrib-head">
          <Tag
            :value="
              c.owner_decision === 'amend'
                ? `owner: amend → ${c.owner_amended_severity}`
                : `owner: ${c.owner_decision}`
            "
            :severity="c.owner_decision === 'nullify' ? 'warn' : 'success'"
          />
          <span
            v-if="c.owner_comment_date"
            class="contrib-time"
            :title="c.owner_comment_date"
          >
            {{ fmt(c.owner_comment_date) }}
          </span>
        </div>
        <div v-if="c.owner_comment" class="contrib-comment">
          <strong>Owner comment:</strong>
          <Markdown :source="c.owner_comment" />
        </div>
      </div>

      <!-- ===== Respond form (two-step) ===== -->
      <template v-if="canIRespond(c)">
        <form
          v-if="respondDraft(c.id).stage === 'idle'"
          class="action-form"
          :data-testid="'respond-form-' + c.id"
          @submit.prevent="startRespond(c.id)"
          @keydown="(e: KeyboardEvent) => isCtrlEnter(e) && startRespond(c.id)"
        >
          <h5 class="form-h">Your response</h5>
          <div class="field">
            <label :for="`respond-decision-${c.id}`">Decision</label>
            <Select
              :id="`respond-decision-${c.id}`"
              v-model="respondDraft(c.id).decision"
              :options="decisionOptions"
              optionLabel="label"
              optionValue="value"
            />
          </div>
          <div class="field">
            <label :for="`respond-comment-${c.id}`">
              Comment
              <span
                v-if="respondDraft(c.id).decision === 'dispute'"
                class="muted"
              > (required)</span>
              <span v-else class="muted">
                (optional — adding one records "Agree, with comment")</span>
            </label>
            <Textarea
              :id="`respond-comment-${c.id}`"
              v-model="respondDraft(c.id).comment"
              rows="4"
              autoResize
              placeholder="What's your take? Markdown supported."
            />
          </div>
          <Message
            v-if="respondDraft(c.id).error"
            severity="error"
            :closable="false"
            size="small"
          >{{ respondDraft(c.id).error }}</Message>
          <div class="actions">
            <Button label="Review response" icon="pi pi-arrow-right" type="submit" />
          </div>
        </form>
        <div
          v-else
          class="action-form confirm"
          :data-testid="'respond-confirm-' + c.id"
          @keydown="(e: KeyboardEvent) => isCtrlEnter(e) && confirmRespond(c.id)"
        >
          <h5 class="form-h">Confirm your response</h5>
          <p class="confirm-line">
            Decision:
            <strong>{{ decisionLabel(effectiveDecision(respondDraft(c.id))) }}</strong>
          </p>
          <p v-if="respondDraft(c.id).comment" class="confirm-comment">
            <strong>Comment:</strong>
            <Markdown :source="respondDraft(c.id).comment" />
          </p>
          <Message severity="warn" :closable="false" size="small">
            Once submitted you cannot edit or withdraw your response.
          </Message>
          <Message
            v-if="respondDraft(c.id).error"
            severity="error"
            :closable="false"
            size="small"
          >{{ respondDraft(c.id).error }}</Message>
          <div class="actions">
            <Button
              label="Back to edit"
              icon="pi pi-arrow-left"
              severity="secondary"
              :disabled="respondDraft(c.id).stage === 'submitting'"
              @click="cancelRespond(c.id)"
            />
            <Button
              label="Confirm and submit"
              icon="pi pi-check"
              :loading="respondDraft(c.id).stage === 'submitting'"
              @click="confirmRespond(c.id)"
            />
          </div>
        </div>
      </template>

      <!-- ===== Finalize form (two-step) ===== -->
      <template v-if="canIFinalize(c)">
        <form
          v-if="finalizeDraft(c.id).stage === 'idle'"
          class="action-form"
          :data-testid="'finalize-form-' + c.id"
          @submit.prevent="startFinalize(c.id)"
          @keydown="(e: KeyboardEvent) => isCtrlEnter(e) && startFinalize(c.id)"
        >
          <h5 class="form-h">Finalise this contributor's response</h5>
          <div class="field">
            <label :for="`fin-decision-${c.id}`">Your decision</label>
            <Select
              :id="`fin-decision-${c.id}`"
              v-model="finalizeDraft(c.id).decision"
              :options="ownerDecisionOptions"
              optionLabel="label"
              optionValue="value"
            />
          </div>
          <div v-if="finalizeDraft(c.id).decision === 'amend'" class="field">
            <label :for="`fin-sev-${c.id}`">New severity</label>
            <Select
              :id="`fin-sev-${c.id}`"
              v-model="finalizeDraft(c.id).amendedSeverity"
              :options="severityOptions"
              optionLabel="label"
              optionValue="value"
            />
          </div>
          <div class="field">
            <label :for="`fin-comment-${c.id}`">Comment (optional)</label>
            <Textarea
              :id="`fin-comment-${c.id}`"
              v-model="finalizeDraft(c.id).comment"
              rows="3"
              autoResize
              placeholder="Optional context for the contributor. Markdown supported."
            />
          </div>
          <Message
            v-if="finalizeDraft(c.id).error"
            severity="error"
            :closable="false"
            size="small"
          >{{ finalizeDraft(c.id).error }}</Message>
          <div class="actions">
            <Button label="Review decision" icon="pi pi-arrow-right" type="submit" />
          </div>
        </form>
        <div
          v-else
          class="action-form confirm"
          :data-testid="'finalize-confirm-' + c.id"
          @keydown="(e: KeyboardEvent) => isCtrlEnter(e) && confirmFinalize(c.id)"
        >
          <h5 class="form-h">Confirm your finalisation</h5>
          <p class="confirm-line">
            Decision:
            <strong>{{ ownerDecisionLabel(finalizeDraft(c.id).decision) }}</strong>
            <template v-if="finalizeDraft(c.id).decision === 'amend'">
              → severity {{ finalizeDraft(c.id).amendedSeverity }}
            </template>
          </p>
          <p v-if="finalizeDraft(c.id).comment" class="confirm-comment">
            <strong>Comment:</strong>
            <Markdown :source="finalizeDraft(c.id).comment" />
          </p>
          <Message severity="warn" :closable="false" size="small">
            Once submitted you cannot edit or withdraw your decision.
          </Message>
          <Message
            v-if="finalizeDraft(c.id).error"
            severity="error"
            :closable="false"
            size="small"
          >{{ finalizeDraft(c.id).error }}</Message>
          <div class="actions">
            <Button
              label="Back to edit"
              icon="pi pi-arrow-left"
              severity="secondary"
              :disabled="finalizeDraft(c.id).stage === 'submitting'"
              @click="cancelFinalize(c.id)"
            />
            <Button
              label="Confirm and submit"
              icon="pi pi-check"
              :loading="finalizeDraft(c.id).stage === 'submitting'"
              @click="confirmFinalize(c.id)"
            />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.event-detail { display: flex; flex-direction: column; gap: 10px; }
.subsection-h {
  font-size: 11px;
  font-weight: 600;
  margin: 12px 0 4px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--p-surface-500);
}
.muted { color: var(--p-surface-500); }
dl.meta {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 6px 16px;
  margin: 4px 0;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--p-surface-200);
  font-size: 13px;
}
dl.meta div { display: flex; gap: 6px; align-items: baseline; }
dl.meta dt { color: var(--p-surface-500); font-weight: 500; flex-shrink: 0; }
dl.meta dd { margin: 0; }
.description {
  margin-top: 8px;
  padding: 4px 2px;
  font-size: 14px;
  line-height: 1.55;
}
.contrib {
  padding: 12px 0;
  border-top: 1px dashed var(--p-surface-200);
}
.contrib:first-of-type { border-top: none; }
.contrib-head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  flex-wrap: wrap;
}
.contrib-email { font-weight: 600; }
.contrib-time {
  font-size: 12px;
  color: var(--p-surface-500);
  margin-left: auto;
}
.contrib-comment {
  margin: 6px 0;
  font-size: 13px;
  padding: 8px 12px;
  background: var(--p-surface-50);
  border-radius: 4px;
}
.contrib-comment strong {
  color: var(--p-surface-600);
  font-size: 12px;
  display: block;
  margin-bottom: 2px;
}
.owner-block {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dotted var(--p-surface-200);
}
.action-form {
  margin-top: 12px;
  padding: 12px;
  background: var(--p-primary-50);
  border-radius: 6px;
  border: 1px solid var(--p-primary-100);
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.action-form.confirm {
  background: var(--p-amber-50);
  border-color: var(--p-amber-200);
}
.form-h {
  font-size: 13px;
  font-weight: 600;
  margin: 0;
  color: var(--p-primary-800);
}
.confirm .form-h { color: var(--p-amber-800); }
.confirm-line { margin: 0; font-size: 13px; }
.confirm-comment {
  margin: 0;
  font-size: 13px;
  padding: 8px 10px;
  background: white;
  border-radius: 4px;
}
.confirm-comment strong {
  display: block;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--p-surface-500);
  margin-bottom: 4px;
}
.field { display: flex; flex-direction: column; gap: 4px; }
.field label {
  font-size: 12px;
  font-weight: 500;
  color: var(--p-surface-700);
}
.actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
